import { useEffect, useMemo, useRef, useState } from "react";
import type { DeskPcSpec, DesktopConfig, MailConfig } from "../scenario/types";
import { Desktop } from "../devices/Desktop";
import { Mail } from "../devices/Mail";
import { WindowTitlebar } from "../devices/WindowTitlebar";
import { audio } from "../lib/audio";
import { tx, txr } from "../lib/tx";

export type PcStage = "login" | "desktop";

/**
 * Full-screen 2D "zoom" of the desk PC once the learner clicks the sticky note
 * in the walkable room. A realistic Windows-11-style login the learner actually
 * TYPES (or pastes) the sticky-note password into; on success a logon chime
 * plays and a simulated desktop appears (same Desktop/Mail components as the
 * phishing-office course) with a browser and a mail app to open.
 */
export function PcScreenOverlay({
  spec,
  onClose,
  onStage,
  onBrowserOpen,
}: {
  spec: DeskPcSpec;
  onClose: () => void;
  /** reports login -> desktop so the top-right task notes can follow along. */
  onStage?: (stage: PcStage) => void;
  /** fired when the learner opens the browser (completes storyboard task 1). */
  onBrowserOpen?: () => void;
}) {
  const title = spec.screenTitle ?? "NorthBay Financial";
  const user = spec.userName ?? "A. Morgan";
  const password = spec.password ?? "Summer2024!";
  const caption = spec.stickyCaption ?? "portal login";

  const [stage, setStage] = useState<PcStage>("login");
  const [typed, setTyped] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [wrong, setWrong] = useState(false);
  const [openApp, setOpenApp] = useState<"browser" | "mail" | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (stage === "login") inputRef.current?.focus();
    onStage?.(stage);
  }, [stage, onStage]);

  const submit = () => {
    if (typed === password) {
      audio.logon();
      setStage("desktop");
    } else {
      setWrong(true);
      setTyped("");
      inputRef.current?.focus();
    }
  };

  const desktopConfig: DesktopConfig = useMemo(() => {
    const now = new Date();
    const hh = now.getHours() % 12 || 12;
    const mm = String(now.getMinutes()).padStart(2, "0");
    const ampm = now.getHours() >= 12 ? "PM" : "AM";
    return {
      clock: `${hh}:${mm} ${ampm}`,
      date: now.toLocaleDateString("en-GB"),
      icons: [
        { id: "browser", label: "Edge", icon: "chrome", opens: "browser" },
        { id: "mail", label: "Mail", icon: "outlook", opens: "mail" },
        { id: "files", label: "File Explorer", icon: "files" },
        { id: "settings", label: "Settings", icon: "settings" },
      ],
      taskbar: [
        { id: "tb-browser", label: "Edge", icon: "chrome", opens: "browser" },
        { id: "tb-mail", label: "Mail", icon: "outlook", opens: "mail" },
        { id: "tb-files", label: "File Explorer", icon: "files" },
      ],
    };
  }, []);

  const mailConfig: MailConfig = useMemo(
    () => ({
      appTitle: "Mail — " + user,
      folders: ["Inbox", "Sent", "Archive", "Deleted"],
      emptyTitle: "Select an email to read it",
      emptyHint: "Nothing here bites. Probably.",
      emails: [
        {
          id: "m1",
          from: "IT Security",
          fromAddress: "security@northbay.example",
          subject: "Reminder: never write passwords down",
          preview: "Sticky notes are not a password manager…",
          body:
            "Hi " + user + ",\n\n" +
            "During last week's clean-desk walk-through we found several passwords " +
            "written on sticky notes attached to monitors.\n\n" +
            "A written password next to the screen can be photographed by any " +
            "visitor, cleaner or shoulder-surfer in seconds. Please use the " +
            "company password manager instead, and enable multi-factor " +
            "authentication on the staff portal.\n\nIT Security",
          unread: true,
          initials: "IT",
        },
        {
          id: "m2",
          from: "Facilities",
          fromAddress: "facilities@northbay.example",
          subject: "Fire drill on Thursday 10:00",
          preview: "Please leave the building via staircase B…",
          body:
            "A routine fire drill is scheduled for Thursday at 10:00. Please " +
            "leave the building via staircase B and gather at the assembly point " +
            "in the courtyard.\n\nFacilities team",
          initials: "FA",
        },
        {
          id: "m3",
          from: "HR — NorthBay",
          fromAddress: "hr@northbay.example",
          subject: "Welcome to the 14th floor",
          preview: "Your desk, badge and coffee machine tour…",
          body:
            "Welcome to the new 14th-floor office! Your badge opens the main " +
            "door between 07:00 and 20:00. The coffee machine tutorial takes " +
            "place every Monday at 9:00 in the kitchen.\n\nHR",
          initials: "HR",
        },
      ],
    }),
    [user],
  );

  return (
    <div className="cf-pc-overlay" role="dialog" aria-modal="true" aria-label={tx("PC screen")}>
      <button type="button" className="cf-pc-close" onClick={onClose} aria-label={tx("Close")}>
        {tx("Back to the room ✕")}
      </button>

      <div className="cf-pc-frame">
        <div className="cf-pc-screen cf-pc-osvars">
          {stage === "login" ? (
            <div className="cf-win-lock">
              <div className={`cf-win-login ${wrong ? "cf-win-login--shake" : ""}`}>
                <div className="cf-win-avatar" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="54" height="54">
                    <circle cx="12" cy="8" r="4" fill="rgba(255,255,255,.9)" />
                    <path d="M4 20c0-4 4-6 8-6s8 2 8 6" fill="rgba(255,255,255,.9)" />
                  </svg>
                </div>
                <div className="cf-win-user">{user}</div>
                <form
                  className="cf-win-pwrow"
                  onSubmit={(e) => {
                    e.preventDefault();
                    submit();
                  }}
                >
                  <div className="cf-win-pwfield">
                    <input
                      ref={inputRef}
                      type="text"
                      className={revealed ? undefined : "cf-mask"}
                      value={typed}
                      placeholder={tx("Password")}
                      aria-label={tx("Password")}
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      spellCheck={false}
                      onChange={(e) => {
                        setTyped(e.target.value);
                        setWrong(false);
                      }}
                    />
                    <button
                      type="button"
                      className="cf-win-eye"
                      aria-label={revealed ? tx("Hide password") : tx("Show password")}
                      onClick={() => {
                        setRevealed((v) => !v);
                        inputRef.current?.focus();
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path
                          d="M12 5c-5 0-9 4.5-10 7 1 2.5 5 7 10 7s9-4.5 10-7c-1-2.5-5-7-10-7z"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.6"
                        />
                        <circle cx="12" cy="12" r="3" fill="currentColor" />
                      </svg>
                    </button>
                  </div>
                  <button type="submit" className="cf-win-go" aria-label={tx("Sign in")}>
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path
                        d="M5 12h13m-6-6 6 6-6 6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </form>
                {wrong ? (
                  <div className="cf-win-error">{tx("The password is incorrect. Try again.")}</div>
                ) : (
                  <div className="cf-win-subtle">{txr("{title} · staff sign-in", { title })}</div>
                )}
                <button type="button" className="cf-win-options">
                  {tx("Sign-in options")}
                </button>
              </div>

              <div className="cf-win-lockbar" aria-hidden="true">
                <span title={tx("Network")}>
                  <svg viewBox="0 0 24 24" width="15" height="15">
                    <path
                      d="M2 9c5.5-5.5 14.5-5.5 20 0M5.5 12.5c3.6-3.6 9.4-3.6 13 0M9 16a4.2 4.2 0 0 1 6 0M12 19.5h.01"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <span title={tx("Accessibility")}>
                  <svg viewBox="0 0 24 24" width="15" height="15">
                    <circle cx="12" cy="5" r="2" fill="currentColor" />
                    <path
                      d="M4 9h16M12 9v6m0 0-3.5 6M12 15l3.5 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <span title={tx("Power")}>
                  <svg viewBox="0 0 24 24" width="15" height="15">
                    <path
                      d="M12 3v8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                    <path
                      d="M7 6a7.4 7.4 0 1 0 10 0"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </div>

              {/* The sticky note stays in view — the whole point of the lesson. */}
              <div className="cf-pc-sticky" style={{ transform: "rotate(-4deg)" }}>
                <div className="cf-pc-sticky-cap">{caption}</div>
                <div className="cf-pc-sticky-pw">{password}</div>
              </div>
            </div>
          ) : (
            <div className="cf-win-desktop-host">
              <Desktop
                config={desktopConfig}
                onOpen={(appId) => {
                  if (appId === "browser" || appId === "mail") setOpenApp(appId);
                  if (appId === "browser") onBrowserOpen?.();
                }}
              />
              {openApp === "mail" && (
                <div className="cf-pc-window">
                  <Mail config={mailConfig} onOpenEmail={() => {}} />
                  <button
                    type="button"
                    className="cf-pc-winclose"
                    aria-label={tx("Close Mail")}
                    onClick={() => setOpenApp(null)}
                  />
                </div>
              )}
              {openApp === "browser" && (
                <div className="cf-pc-window">
                  <div className="winapp cf-pc-browser">
                    <WindowTitlebar title={tx("Microsoft Edge")} />
                    <div className="cf-pc-browser-bar">
                      <span className="cf-pc-browser-nav" aria-hidden="true">
                        <i>←</i>
                        <i>→</i>
                        <i>⟳</i>
                      </span>
                      <span className="cf-pc-browser-url">
                        <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                          <rect
                            x="5"
                            y="10"
                            width="14"
                            height="10"
                            rx="2"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          />
                          <path
                            d="M8 10V7a4 4 0 0 1 8 0v3"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          />
                        </svg>
                        https://intranet.northbay.example
                      </span>
                    </div>
                    <div className="cf-pc-browser-page">
                      <div className="cf-pc-intranet-head">
                        <strong>{tx("NorthBay Intranet")}</strong>
                        <span>{txr("Signed in as {user}", { user })}</span>
                      </div>
                      <div className="cf-pc-intranet-cards">
                        <div className="cf-pc-intranet-card">
                          <h5>{tx("IT Security tips")}</h5>
                          <p>
                            {tx("Use the password manager. Never write credentials on paper — and never on a sticky note next to your screen.")}
                          </p>
                        </div>
                        <div className="cf-pc-intranet-card">
                          <h5>{tx("HR portal")}</h5>
                          <p>{tx("Holiday requests, payslips and the new-starter checklist.")}</p>
                        </div>
                        <div className="cf-pc-intranet-card">
                          <h5>{tx("Helpdesk")}</h5>
                          <p>{tx("Report incidents or suspicious emails to the SOC in one click.")}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="cf-pc-winclose"
                    aria-label={tx("Close browser")}
                    onClick={() => setOpenApp(null)}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {stage === "login" && (
          <div className="cf-pc-lesson">
            {txr("<0>Awareness moment.</0> A password on a sticky note next to the screen is one of the easiest credentials to steal — a visitor, a cleaner, or a shoulder-surfer photographs it in seconds. Use a password manager and never write credentials where they can be seen.", {}, [<strong />])}
          </div>
        )}
      </div>
    </div>
  );
}
